import React from 'react';
import PropTypes from 'prop-types';
import { LuBuilding2, LuLoaderCircle, LuTrash2, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import { isArchivedAssignment } from '../../../utils/assignmentHistory';
import { formatLeftDate, roleLabel } from './workerOptions';

/**
 * "Projeden Tamamen Çıkar" onayı (yalnızca ana admin). Geri alınamaz olduğu
 * için silinecek atamalar tek tek listelenir.
 */
const WorkerRemoveDialog = ({ worker, projectTitle, removing, onConfirm, onCancel }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`projectManagement.workers.remove.${key}`, vars);
  const name = worker ? `${worker.name || ''} ${worker.surname || ''}`.trim() : '';

  return (
    <Modal
      isOpen={Boolean(worker)}
      onClose={() => !removing && onCancel()}
      size="md"
      title={label('title')}
      subtitle={name}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-rose-400 tw-to-rose-600 tw-shadow-rose-500/30" size="xs">
          <LuTrash2 aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onCancel} disabled={removing}>{label('cancel')}</Button>
          <Button variant="danger" onClick={onConfirm} disabled={removing} className="max-sm:tw-flex-1">
            {removing
              ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
              : <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {removing ? label('removing') : label('confirm')}
          </Button>
        </>
      )}
    >
      {worker && (
        <div className="tw-space-y-4">
          <p className="tw-text-[15px] tw-leading-relaxed tw-text-slate-700 dark:tw-text-slate-200">
            {label('lead', { name, project: projectTitle || '' })}
          </p>
          <ul className="tw-max-h-56 tw-space-y-1.5 tw-overflow-y-auto tw-overscroll-contain glass-scrollbar">
            {worker.centers.map((center, index) => {
              const leftOn = formatLeftDate(center, locale);
              return (
                <li
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${center.centerId}-${center.role}-${index}`}
                  className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2 tw-text-sm dark:tw-border-white/10 dark:tw-bg-white/[0.03]"
                >
                  <LuBuilding2 aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                  <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-slate-800 dark:tw-text-slate-100">
                    {center.centerName || t('projectManagement.workers.detail.unknownCenter')}
                    {center.role && <span className="tw-text-slate-500 dark:tw-text-slate-400">{` · ${roleLabel(t, center.role)}`}</span>}
                  </span>
                  {isArchivedAssignment(center) && (
                    <span className="tw-shrink-0 tw-rounded-full tw-bg-amber-500/10 tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-text-amber-700 dark:tw-text-amber-300">
                      {leftOn ? t('projectManagement.workers.detail.leftOn', { date: leftOn }) : t('projectManagement.workers.status.left')}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-3 tw-text-sm tw-leading-relaxed tw-text-rose-800 dark:tw-text-rose-200">
            <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0" />
            {label('warning')}
          </p>
        </div>
      )}
    </Modal>
  );
};

WorkerRemoveDialog.propTypes = {
  worker: PropTypes.shape({
    name: PropTypes.string,
    surname: PropTypes.string,
    centers: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  projectTitle: PropTypes.string,
  removing: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default WorkerRemoveDialog;
